import React, { useState } from 'react';
import { LayoutDashboard, Users, Building, FileText, Upload, Download, Filter, Plus, BarChart3 } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { MessageModal } from '../components/modals/MessageModal';
import { AddPlotModal } from '../components/modals/AddPlotModal';
export function DeveloperDashboardPage() {
  const navigate = useNavigate();
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [isAddPlotModalOpen, setIsAddPlotModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    type: 'info' as 'success' | 'error' | 'info'
  });
  const handleAddPlot = () => {
    setIsAddPlotModalOpen(true);
  };
  const handleBulkUpload = () => {
    setModalContent({
      title: 'Bulk Upload',
      message: 'Bulk upload feature is coming soon. You will be able to upload multiple plots via CSV file with all details.',
      type: 'info'
    });
    setIsMessageModalOpen(true);
  };
  const handleViewLeads = (plotNo: string, leadCount: number) => {
    if (leadCount > 0) {
      navigate('/developer/leads');
    }
  };
  const handleManagePlot = (plotNo: string) => {
    navigate('/developer/inventory');
  };
  const handleDownloadReport = () => {
    setModalContent({
      title: 'Downloading Report',
      message: 'Generating inventory report... In production, this would download a comprehensive Excel/PDF report.',
      type: 'success'
    });
    setIsMessageModalOpen(true);
  };
  const handleFilterInventory = () => {
    setModalContent({
      title: 'Filter Inventory',
      message: 'Advanced filtering options coming soon. Filter by status, price range, size, and more.',
      type: 'info'
    });
    setIsMessageModalOpen(true);
  };
  const navItems = [{
    label: 'Overview',
    icon: LayoutDashboard,
    path: '/dashboard/developer'
  }, {
    label: 'Inventory',
    icon: Building,
    path: '/developer/inventory'
  }, {
    label: 'Leads & CRM',
    icon: Users,
    path: '/developer/leads',
    badge: '3'
  }, {
    label: 'Reports',
    icon: FileText,
    path: '/developer/reports'
  }];
  return <DashboardLayout title="Developer Console" personaLabel="DEVELOPER" personaColor="bg-blue-100 text-blue-800 border-blue-200" navItems={navItems} userInitials="DL" data-id="element-4011">
      <div data-testid="developer-dashboard-page" className="space-y-8" data-id="element-4012">
        <div data-testid="developer-dashboard-header" className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8" data-id="element-4013">
          <div data-id="element-4014">
            <h1 data-testid="developer-dashboard-title" className="text-2xl font-bold mb-1" data-id="element-4015">
              Green Valley Phase 1
            </h1>
            <p data-testid="developer-dashboard-subtitle" className="text-gray-500 font-mono text-sm" data-id="element-4016">
              Project ID: DEV-8821 • Hinjewadi, Pune
            </p>
          </div>
          <div className="flex gap-3" data-id="element-4017">
            <button data-testid="developer-dashboard-bulk-upload-btn" onClick={handleBulkUpload} className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white text-sm font-medium hover:bg-gray-50" data-id="element-4018">
              <Upload className="w-4 h-4" data-id="element-4019" /> Bulk Upload
            </button>
            <button data-testid="developer-dashboard-add-plot-btn" onClick={handleAddPlot} className="flex items-center gap-2 px-4 py-2 bg-[#1f2933] text-white text-sm font-bold hover:bg-gray-800" data-id="element-4020">
              <Plus className="w-4 h-4" data-id="element-4021" /> Add Plot
            </button>
          </div>
        </div>

        <div data-testid="developer-dashboard-stats" className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-id="element-4022">
          {[{
          label: 'Total Inventory',
          value: '142',
          sub: 'Plots',
          testid: 'inventory'
        }, {
          label: 'Sold / Booked',
          value: '86',
          sub: '60% Sold',
          testid: 'sold'
        }, {
          label: 'Active Leads',
          value: '24',
          sub: 'High Intent',
          link: '/developer/leads',
          testid: 'leads'
        }, {
          label: 'Revenue (YTD)',
          value: '₹12.4Cr',
          sub: '+15% vs Target',
          testid: 'revenue'
        }].map((stat, i) => <div key={i} data-testid={`developer-dashboard-${stat.testid}-card`} className={`bg-white border border-gray-300 p-6 ${stat.link ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`} onClick={() => stat.link && navigate(stat.link)} data-id="element-4023">
              <h3 className="text-xs font-mono text-gray-500 uppercase mb-2" data-id="element-4024">
                {stat.label}
              </h3>
              <div className="flex items-baseline gap-2" data-id="element-4025">
                <span data-testid={`developer-dashboard-${stat.testid}-value`} className="text-3xl font-bold" data-id="element-4026">
                  {stat.value}
                </span>
                <span className="text-xs font-mono text-green-600" data-id="element-4027">
                  {stat.sub}
                </span>
              </div>
            </div>)}
        </div>

        <div data-testid="developer-dashboard-inventory-table" className="bg-white border border-gray-300 mb-8" data-id="element-4028">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50" data-id="element-4029">
            <h2 className="font-bold text-lg" data-id="element-4030">Plot Inventory</h2>
            <div className="flex gap-2" data-id="element-4031">
              <button data-testid="developer-dashboard-filter-btn" onClick={handleFilterInventory} className="p-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-sm" data-id="element-4032">
                <Filter className="w-4 h-4 text-gray-600" data-id="element-4033" />
              </button>
              <button data-testid="developer-dashboard-download-btn" onClick={handleDownloadReport} className="p-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-sm" data-id="element-4034">
                <Download className="w-4 h-4 text-gray-600" data-id="element-4035" />
              </button>
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-4036">
            <table className="w-full text-left text-sm min-w-[800px]" data-id="element-4037">
              <thead className="bg-gray-50 border-b border-gray-200 font-mono text-xs uppercase text-gray-500" data-id="element-4038">
                <tr data-id="element-4039">
                  <th className="p-4 font-bold" data-id="element-4040">Plot No.</th>
                  <th className="p-4 font-bold" data-id="element-4041">Size (Sq.Ft)</th>
                  <th className="p-4 font-bold" data-id="element-4042">Facing</th>
                  <th className="p-4 font-bold" data-id="element-4043">Price</th>
                  <th className="p-4 font-bold" data-id="element-4044">Status</th>
                  <th className="p-4 font-bold" data-id="element-4045">Leads</th>
                  <th className="p-4 font-bold text-right" data-id="element-4046">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-4047">
                {[{
                no: 'A-101',
                size: '2,400',
                face: 'East',
                price: '₹45L',
                status: 'Available',
                leads: 3
              }, {
                no: 'A-102',
                size: '2,400',
                face: 'East',
                price: '₹45L',
                status: 'Booked',
                leads: 0
              }, {
                no: 'B-205',
                size: '1,800',
                face: 'North',
                price: '₹38L',
                status: 'Available',
                leads: 5
              }, {
                no: 'C-301',
                size: '3,000',
                face: 'West',
                price: '₹52L',
                status: 'Sold',
                leads: 0
              }, {
                no: 'C-302',
                size: '3,000',
                face: 'West',
                price: '₹52L',
                status: 'Available',
                leads: 1
              }].map((plot, i) => <tr key={i} data-testid={`developer-dashboard-plot-row-${i + 1}`} className="hover:bg-gray-50" data-id="element-4048">
                    <td className="p-4 font-bold" data-id="element-4049">{plot.no}</td>
                    <td className="p-4 font-mono" data-id="element-4050">{plot.size}</td>
                    <td className="p-4" data-id="element-4051">{plot.face}</td>
                    <td className="p-4 font-bold" data-id="element-4052">{plot.price}</td>
                    <td className="p-4" data-id="element-4053">
                      <span data-testid={`developer-dashboard-plot-row-${i + 1}-status`} className={`inline-block px-2 py-0.5 text-xs font-bold rounded-sm border ${plot.status === 'Available' ? 'bg-green-100 text-green-800 border-green-200' : plot.status === 'Booked' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`} data-id="element-4054">
                        {plot.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4" data-id="element-4055">
                      {plot.leads > 0 ? <button data-testid={`developer-dashboard-plot-row-${i + 1}-leads-btn`} onClick={() => handleViewLeads(plot.no, plot.leads)} className="text-blue-600 font-bold underline cursor-pointer hover:text-blue-800" data-id="element-4056">
                          {plot.leads} Leads
                        </button> : <span className="text-gray-400" data-id="element-4057">-</span>}
                    </td>
                    <td className="p-4 text-right" data-id="element-4058">
                      <button data-testid={`developer-dashboard-plot-row-${i + 1}-manage-btn`} onClick={() => handleManagePlot(plot.no)} className="text-gray-500 hover:text-[#1f2933] font-bold text-xs" data-id="element-4059">
                        Manage
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-gray-200 bg-gray-50 text-center" data-id="element-4060">
            <button data-testid="developer-dashboard-view-all-btn" onClick={() => navigate('/developer/inventory')} className="text-sm font-bold text-[#1f2933] hover:underline" data-id="element-4061">
              View All 142 Plots
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-4062">
          <div data-testid="developer-dashboard-lead-funnel" className="bg-white border border-gray-300 p-6" data-id="element-4063">
            <h3 className="font-bold text-lg mb-6" data-id="element-4064">Lead Funnel</h3>
            <div className="space-y-4" data-id="element-4065">
              {[{
              stage: 'New Inquiries',
              count: 45,
              pct: 100,
              color: 'bg-blue-500'
            }, {
              stage: 'Site Visits Scheduled',
              count: 28,
              pct: 62,
              color: 'bg-blue-600'
            }, {
              stage: 'Negotiation / Token',
              count: 12,
              pct: 26,
              color: 'bg-blue-700'
            }, {
              stage: 'Closed / Booked',
              count: 8,
              pct: 17,
              color: 'bg-green-600'
            }].map((item, i) => <div key={i} data-testid={`developer-dashboard-funnel-stage-${i + 1}`} data-id="element-4066">
                  <div className="flex justify-between text-sm mb-1" data-id="element-4067">
                    <span className="font-medium" data-id="element-4068">{item.stage}</span>
                    <span className="font-mono font-bold" data-id="element-4069">{item.count}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4070">
                    <div className={`h-full ${item.color}`} style={{
                  width: `${item.pct}%`
                }} data-id="element-4071"></div>
                  </div>
                </div>)}
            </div>
          </div>

          <div data-testid="developer-dashboard-sales-velocity" className="bg-white border border-gray-300 p-6 flex flex-col" data-id="element-4072">
            <h3 className="font-bold text-lg mb-6" data-id="element-4073">Sales Velocity</h3>
            <div className="flex-1 border-2 border-dashed border-gray-200 flex items-center justify-center bg-gray-50" data-id="element-4074">
              <div className="text-center text-gray-400" data-id="element-4075">
                <BarChart3 className="w-8 h-8 mx-auto mb-2" data-id="element-4076" />
                <span className="font-mono text-sm" data-id="element-4077">
                  [Sales Trend Chart Placeholder]
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddPlotModal isOpen={isAddPlotModalOpen} onClose={() => setIsAddPlotModalOpen(false)} projectName="Green Valley Phase 1" data-id="element-4078" />

      <MessageModal isOpen={isMessageModalOpen} onClose={() => setIsMessageModalOpen(false)} title={modalContent.title} message={modalContent.message} type={modalContent.type} data-id="element-4079" />
    </DashboardLayout>;
}